import { downloadFile, stringifyURLQuery, uniq } from '@seiue/util'
import { Boot, IDomEditor } from '@wangeditor/editor'
import { UploadFile } from 'antd/es/upload/interface'

import { ActionType, importData } from 'packages/components/Import'
import {
  FileDirName,
  NotificationReceiver,
  UserForegroundDomainEnum,
  notificationApi,
} from 'packages/sdks-next/growp'
import { AnyType } from 'packages/types'
import { uploadFile, env } from 'packages/utils'

import { reflectionStorage, store } from '@/store'

import { selectedReceiverIds } from './data'

export type NotificationAttachment = {
  url: string
  name: string
  size?: number
}

export const variables: string[] = []
export const receivers: NotificationReceiver[] = []
export let attachment: NotificationAttachment | undefined = undefined
export let reflectionDomain = UserForegroundDomainEnum.Informal

/**
 * 重置变量
 *
 * @param value - 重置数据
 */
export const setVariables = (value: string[]) => {
  // 重置变量
  variables.splice(0, variables.length, ...value)
}

/**
 * 重置 receivers 数据
 *
 * @param value - 重置数据
 */
export const setReceivers = (value: NotificationReceiver[]) => {
  // 重置变量
  receivers.splice(0, receivers.length, ...value)
}

/**
 * 重置 attachment 数据
 *
 * @param value - 重置数据
 */
export const setAttachment = (
  value: { url: string; name: string; size?: number } | undefined
) => {
  attachment = value
}

/**
 * 重置 reflectionDomain 数据
 *
 * @param value - 重置数据
 */
export const setReflectionDomain = (value: UserForegroundDomainEnum) => {
  reflectionDomain = value
}

// UploadFile to File
const uploadFileToFile = (file: UploadFile) => {
  const { name, type, lastModified } = file
  return new File([file as AnyType], name, {
    type,
    lastModified,
  })
}

// 通知变量的 svg
const variablesSvg =
  '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" fill="none" version="1.1" width="12" height="13.333333969116211" viewBox="0 0 12 13.333333969116211"><g><path d="M0,1C0,0.447716,0.447715,0,1,0C1,0,11,0,11,0C11.5523,0,12,0.447715,12,1C12,1,12,12.3333,12,12.3333C12,12.8856,11.5523,13.3333,11,13.3333C11,13.3333,1,13.3333,1,13.3333C0.447715,13.3333,0,12.8856,0,12.3333C0,12.3333,0,1,0,1ZM1.33333,1.33333C1.33333,1.33333,1.33333,12,1.33333,12C1.33333,12,10.6667,12,10.6667,12C10.6667,12,10.6667,1.33333,10.6667,1.33333C10.6667,1.33333,1.33333,1.33333,1.33333,1.33333ZM4.27614,6.69036C4.27614,6.69036,6.16176,4.80474,6.16176,4.80474C6.16176,4.80474,5.21895,3.86193,5.21895,3.86193C5.21895,3.86193,2.39052,6.69036,2.39052,6.69036C2.39052,6.69036,5.21895,9.51878,5.21895,9.51878C5.21895,9.51878,6.16176,8.57597,6.16176,8.57597C6.16176,8.57597,4.27614,6.69036,4.27614,6.69036ZM6.66651,9.1516C6.66651,9.1516,8.04688,4,8.04688,4C8.04688,4,9.33478,4.34509,9.33478,4.34509C9.33478,4.34509,7.95441,9.4967,7.95441,9.4967C7.95441,9.4967,6.66651,9.1516,6.66651,9.1516Z" fill-rule="evenodd" fill="#455766" fill-opacity="1"/></g></svg>'

export const variablesMenuConfig = {
  key: 'importVariables',
  factory: () => ({
    title: attachment ? '更新变量' : '导入变量',
    iconSvg: variablesSvg,
    tag: 'button',
    alwaysEnable: true,
    getValue: () => '导入变量',
    isActive: () => false,
    isDisabled: () => false,
    exec: () => {
      importData({
        title: attachment ? '更新变量' : '导入变量',
        action: attachment ? ActionType.Update : ActionType.Create,
        onClickDownload: async () => {
          if (attachment) {
            downloadFile(attachment.name, attachment.url)
            return
          }

          const accessToken = store.get(reflectionStorage)?.token || ''
          const query = stringifyURLQuery({
            _authorization: `${accessToken}`,
          })

          downloadFile(
            '通知变量模板.xlsx',
            `${env(
              'SERVER_GROWP'
            )}/notification/variable/download-template?${query}&id_in=${selectedReceiverIds.join(
              ','
            )}&reflection_domain=${reflectionDomain}&filename=通知变量模板`
          )
        },
        uploadRemote: async (file) => {
          const { data } = await notificationApi.parseVariableFile(
            reflectionDomain,
            { file }
          )

          const formatFile = uploadFileToFile(file)
          const { data: uploadedFile } = await uploadFile({
            file: formatFile,
            dirname: FileDirName.File,
          })

          attachment = {
            url: uploadedFile?.url || '',
            name: file.name,
            size: file.size,
          }
          setVariables(data.columns)
          setReceivers(data.receivers)
          return Promise.resolve(!!data)
        },
      })
    },
  }),
}

export const insertVariablesMenuConfig = {
  key: 'insertVariables',
  factory: () => ({
    title: '插入变量',
    tag: 'select',
    // 下拉框的选项
    getOptions() {
      const options = uniq(variables).map((item) => {
        return {
          text: item,
          value: item,
        }
      })
      return [
        ...options,
        // 用来展示
        {
          text: '插入变量',
          value: '插入变量',
          styleForRenderMenuList: { display: 'none' },
        },
      ]
    },
    getValue: () => '插入变量',
    isActive: () => false,
    isDisabled: () => !variables.length,
    exec: (editor: IDomEditor, value: string | boolean) => {
      if (typeof value === 'boolean') return
      editor.insertText(`{{${value}}}`)
    },
  }),
}

Boot.registerMenu(variablesMenuConfig)

Boot.registerMenu(insertVariablesMenuConfig)
